import React from 'react';
import { IconColor, SalvButton, Size } from '@salvapp/ui-components';
import { useDispatch } from 'react-redux';
import { InternalApiReviewRequestEntityType, InternalApiReviewStatus } from '../../api-internal';
import { useReviewRequest, useResolveReviewRequest } from '../../review-requests/reviewRequestActions';
import { useMe } from '../../organisation/useMe';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import Has from '../../auth/Has';
import { CAN_MODIFY_REVIEW_REQUESTS } from '../../auth/permissions';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_TEMPLATE_KEY, QUERY_TEMPLATE_UPDATE_KEY } from './queryTemplatesActions';

interface Props {
  handle: string;
  onResolved: () => void;
}

/**
 * Review actions for a query template that is waiting for a second pair of eyes: its author can only
 * withdraw the change, anyone else with the permission can approve or reject it.
 */
export const QueryTemplateReviewActions = ({ handle, onResolved }: Props) => {
  const dispatch = useDispatch();
  const { id: currentUserId, currentOrganisation } = useMe();
  const { data: reviewRequest } = useReviewRequest(InternalApiReviewRequestEntityType.QUERY_TEMPLATE, handle);
  const queryClient = useQueryClient();
  const { mutate: resolveReviewRequest, isPending } = useResolveReviewRequest(onResolved, () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_KEY, currentOrganisation?.id] });
    queryClient.invalidateQueries({ queryKey: [QUERY_TEMPLATE_UPDATE_KEY, handle, currentOrganisation?.id] });
  });

  if (!handle || !reviewRequest) {
    return null;
  }

  const resolve = (status: InternalApiReviewStatus) => {
    resolveReviewRequest({
      entityId: handle,
      entityType: InternalApiReviewRequestEntityType.QUERY_TEMPLATE,
      status,
    });
  };

  const confirmAndResolve = (status: InternalApiReviewStatus, title: string, confirmationText: string) =>
    dispatch(
      showConfirmationModal({
        title,
        confirmationText,
        cancelText: 'No',
        iconColor: status === InternalApiReviewStatus.APPROVED ? IconColor.SUCCESS : IconColor.DANGER,
        confirmButtonVariant: status === InternalApiReviewStatus.APPROVED ? 'primary' : 'danger',
        onConfirm: async () => resolve(status),
      })
    );

  const isAuthor = currentUserId !== undefined && currentUserId === reviewRequest.createdBy.id;

  if (isAuthor) {
    return (
      <SalvButton
        size={Size.LARGE}
        variant='danger'
        disabled={isPending}
        label='Withdraw'
        onClick={() =>
          confirmAndResolve(
            InternalApiReviewStatus.WITHDRAWN,
            'Are you sure you want to withdraw this change?',
            'Yes, withdraw'
          )
        }
      />
    );
  }

  return (
    <Has allPermissions={CAN_MODIFY_REVIEW_REQUESTS}>
      <SalvButton
        size={Size.LARGE}
        variant='danger'
        disabled={isPending}
        label='Reject'
        onClick={() =>
          confirmAndResolve(
            InternalApiReviewStatus.REJECTED,
            'Are you sure you want to reject this change?',
            'Yes, reject'
          )
        }
      />
      <SalvButton
        size={Size.LARGE}
        variant='primary'
        disabled={isPending}
        label='Approve'
        onClick={() =>
          confirmAndResolve(
            InternalApiReviewStatus.APPROVED,
            'Are you sure you want to approve this change?',
            'Yes, approve'
          )
        }
      />
    </Has>
  );
};

export default QueryTemplateReviewActions;
